import React, { useState } from "react";
import Markdown from "react-markdown";
import moment from "moment";
import { makeStyles } from "@material-ui/styles";
import Chip from "@material-ui/core/Chip";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Typography from "@material-ui/core/Typography";
import { useSelector } from "react-redux";
import { CloseButton } from "@mantine/core";
import { STATE_CULTURE } from "../Actions/stateActions";
import { getDuration } from "./util";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "350px",
    "@media(max-width: 667px)": {
      width: "100%",
    },
  },
  tabText: {
    color: "black",
  },
  header: {
    background: theme.app.background[1],
    color: theme.app.color,
    position: "sticky",
    top: 0,
    zIndex: 1000,
  },
  mainHeaderText: {
    padding: "8px 16px",
    fontSize: "1.25rem !important",
    fontWeight: 500,
  },
  subHeaderText: { padding: "8px 16px", fontSize: "1rem !important" },
  contentContainer: {},
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    padding: "0 8px",
    margin: "16px 0",
  },
  tab: {
    opacity: 1,
  },
  tag: {
    marginRight: "8px",
  },
  tabContent: {
    padding: "16px",
  },
  at: {
    color: theme.app.color,
    fontSize: "0.875rem",
  },
  duration: {
    color: theme.app.color,
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  button: {
    margin: ".5rem",
    borderRadius: "50%",
    color: theme.app.labelColor,
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.easeInOut,
    }),
    "&:hover": {
      backgroundColor: theme.app.hoverBackground[1],
    },
  },
  infoText: { fontSize: "0.8125rem", color: "theme.app.secondaryColor" },
}));

const TabContainer = ({ children }) => (
  <Typography component="div">{children}</Typography>
);

const SideInfo = (props) => {
  const { culture, language } = useSelector((state) => ({
    culture: state.cookies[STATE_CULTURE],
    language: state.language,
  }));
  const { open, onClose, activity, occasion } = props;
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const handleChange = (event, value) => setValue(value);
  const orderedOccasions =
    activity.occasions &&
    activity.occasions.slice().sort((a, b) => new Date(a.at) - new Date(b.at));

  const activeLocale = culture.split("-")[0];
  return (
    <Drawer
      classes={{ paper: classes.paper }}
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <main>
        <div className={classes.header}>
          <CloseButton
            className={classes.button}
            onClick={onClose}
            aria-label={language.closeActivityMenu}
          />
          <Typography className={classes.mainHeaderText} variant="h1">
            {activity.name}
          </Typography>
          {occasion && (
            <Typography className={classes.subHeaderText} variant="h2">
              {moment(occasion.at).locale(activeLocale).format("LLLL")}
            </Typography>
          )}

          {activity.tags && (
            <div className={classes.tagsContainer}>
              {activity.tags.map((t, i) => (
                <Chip key={i} label={t} className={classes.tag} />
              ))}
            </div>
          )}

          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            variant="fullWidth"
          >
            <Tab className={classes.tab} label="Information" />
            {orderedOccasions && (
              <Tab className={classes.tab} label={language.occasions} />
            )}
          </Tabs>
        </div>
        <div className={classes.tabContent}>
          {value === 0 && (
            <TabContainer>
              <Markdown
                source={activity.infoText || ""}
                escapeHtml={false}
                skipHtml={false}
                className={classes.infoText}
              />
            </TabContainer>
          )}
          {value === 1 && (
            <TabContainer>
              <List>
                {orderedOccasions.map((o, i) => {
                  const at = moment(o.at).locale(activeLocale).format("LLLL");
                  const duration = getDuration(
                    o.duration,
                    activeLocale,
                    language
                  );
                  return (
                    <ListItem key={i} divider={true}>
                      <ListItemText
                        className={classes.tabText}
                        classes={{
                          primary: classes.at,
                          secondary: classes.duration,
                        }}
                        primary={at}
                        secondary={duration}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </TabContainer>
          )}
        </div>
      </main>
    </Drawer>
  );
};

export default SideInfo;
